import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-button';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import { t, LocaleController } from '@vitesse/base-ui/i18n';
import './vitesse-dashboard-background-field';
import type { DashboardManager } from '../managers/dashboard-manager';
import type { Dashboard, DashboardBackground } from '../../core/domain/dashboard';

/**
 * <vitesse-create-dashboard-dialog> — making an empty board, by hand.
 *
 * Until this existed the ONLY way to create a dashboard was to ask the assistant, because
 * `dashboards.create` is a tool and nothing in the UI called it. The empty state said so out loud
 * ("Ask the AI to create one, or import a board file"), which is a reasonable thing to offer and a
 * poor thing to require: a person who wants a blank board to drag tiles onto should not have to
 * describe it to a model first.
 *
 * TITLE, DESCRIPTION AND THE PICTURE BEHIND IT — the same three things `Edit` offers, because they
 * are the same three things a board IS. Splitting them (name it now, decorate it later) made the
 * two dialogs disagree about what a dashboard has, which is a worse answer than one field nobody
 * fills in. The description is not decoration either: it is what the assistant reads to know what
 * the board is for.
 *
 * The id is derived from the title rather than asked for. It has to be unique on the gateway and it
 * is never shown, so making a person invent one would be asking them to solve the machine's problem.
 */
@customElement('vitesse-create-dashboard-dialog')
export class VitesseCreateDashboardDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ attribute: false }) dashboards?: DashboardManager;
  /** Ids already on this gateway — the id derived from the title must miss all of them. */
  @property({ attribute: false }) existingIds: readonly string[] = [];

  @state() private boardTitle = '';
  @state() private description = '';
  @state() private background?: DashboardBackground;
  @state() private busy = false;
  @state() private error = '';

  constructor() {
    super();
    new LocaleController(this); // re-render on language change
  }


  static styles = css`
    .field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
    label { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
    .error { color: var(--red); font-size: 13px; margin: 0 0 4px; }
  `;

  /**
   * A url-safe id from the title, made unique against what the gateway already has.
   *
   * Slugified rather than random so a board is recognisable in a log, an export or a URL. The
   * numeric suffix only appears on a real collision — two boards genuinely called the same thing —
   * which is rare enough that the common case stays clean.
   */
  private idFor(title: string): string {
    const base = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48) || 'dashboard';
    const taken = new Set(this.existingIds);
    if (!taken.has(base)) return base;
    for (let n = 2; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
  }

  private close = () => {
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  };

  /** Cleared on close so reopening is a fresh board rather than the last attempt. */
  private reset(): void {
    this.boardTitle = '';
    this.description = '';
    this.background = undefined;
    this.error = '';
  }

  private async submit(): Promise<void> {
    const title = this.boardTitle.trim();
    if (!title) {
      this.error = t('dashboards.titleNeeded');
      return;
    }
    if (!this.dashboards || this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      // An empty board: no tiles, no background, no digest — the gateway mints the digest it will
      // then demand back on every save.
      const board: Dashboard = {
        id: this.idFor(title),
        title,
        description: this.description.trim(),
        digest: '',
        tileCount: 0,
        updatedAtMs: Date.now(),
        tiles: [],
        background: this.background,
      };
      const r = await this.dashboards.create(board);
      if (!r.ok) {
        this.error = r.error || t('dashboards.createFailed');
        return;
      }
      // The roster stream delivers the new board, so there is nothing to hand back but its id —
      // which the page uses to navigate straight onto it.
      this.dispatchEvent(new CustomEvent('created', { detail: { id: board.id }, bubbles: true, composed: true }));
      this.reset();
      this.close();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.busy = false;
    }
  }

  render() {
    return html`<vitesse-dialog .open=${this.open} heading=${t('dashboards.createHeading')} @dialog-close=${this.close}>
      ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
      <div class="field">
        <label for="title">${t('dashboards.titleLabel')}</label>
        <vitesse-text-input
          id="title"
          .value=${this.boardTitle}
          placeholder=${t('dashboards.titlePlaceholder')}
          @input=${(e: Event) => (this.boardTitle = (e.target as HTMLInputElement).value)}
        ></vitesse-text-input>
      </div>
      <div class="field">
        <label for="description">${t('dashboards.descriptionLabel')}</label>
        <vitesse-text-input
          id="description"
          multiline
          rows="3"
          .value=${this.description}
          placeholder=${t('dashboards.descriptionPlaceholder')}
          @input=${(e: Event) => (this.description = (e.target as HTMLTextAreaElement).value)}
        ></vitesse-text-input>
      </div>
      <vitesse-dashboard-background-field
        .background=${this.background}
        @background-change=${(e: CustomEvent<{ background?: DashboardBackground }>) => (this.background = e.detail.background)}
        @background-error=${(e: CustomEvent<{ message: string }>) => (this.error = e.detail.message)}
      ></vitesse-dashboard-background-field>
      <vitesse-button slot="footer" color="red" @click=${this.close}>${t('dashboards.cancel')}</vitesse-button>
      <vitesse-button slot="footer" color="blue" ?disabled=${this.busy || !this.boardTitle.trim()} @click=${() => void this.submit()}>
        ${this.busy ? t('dashboards.creating') : t('dashboards.createConfirm')}
      </vitesse-button>
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-create-dashboard-dialog': VitesseCreateDashboardDialog;
  }
}
